/**
 * Users Page
 * Admin user management with beta tester and admin controls
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Loading, ErrorMessage } from '@/components/common';
import { usersApi } from '@/lib/api/users';
import { useI18n } from '@/i18n';
import type { AdminUserListItem, AdminUserListParams, UserRoleLevel } from '@/types/users';
import { UserResourcesModal } from '@/features/resources';
import {
  Users,
  Search,
  FlaskConical,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  UserCheck,
  UserX,
  Shield,
  ShieldOff,
  Crown,
  ChevronDown,
  FolderOpen,
} from 'lucide-react';

const ROLE_COLORS: Record<UserRoleLevel, string> = {
  free: 'bg-gray-100 text-gray-800',
  vip: 'bg-yellow-100 text-yellow-800',
  premium: 'bg-purple-100 text-purple-800',
};

export function UsersPage() {
  const { messages } = useI18n();
  const t = messages.users;
  const queryClient = useQueryClient();
  const [params, setParams] = useState<AdminUserListParams>({
    page: 1,
    page_size: 20,
  });
  const [search, setSearch] = useState('');
  const [filterBetaTester, setFilterBetaTester] = useState<boolean | undefined>(undefined);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [resourcesModalUser, setResourcesModalUser] = useState<{ userId: string; userName?: string } | null>(null);

  // Fetch users
  const {
    data: usersData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['admin-users', params, filterBetaTester],
    queryFn: () => usersApi.listUsers({ ...params, is_beta_tester: filterBetaTester }),
  });

  // Fetch comprehensive user stats
  const { data: stats } = useQuery({
    queryKey: ['user-stats'],
    queryFn: usersApi.getUserStats,
  });

  // Update beta tester status mutation
  const updateBetaTesterMutation = useMutation({
    mutationFn: ({ userId, isBetaTester }: { userId: string; isBetaTester: boolean }) =>
      usersApi.updateBetaTesterStatus(userId, isBetaTester),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['user-stats'] });
    },
  });

  // Batch update mutation
  const batchUpdateMutation = useMutation({
    mutationFn: ({ userIds, isBetaTester }: { userIds: string[]; isBetaTester: boolean }) =>
      usersApi.batchUpdateBetaTesters(userIds, isBetaTester),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['user-stats'] });
      setSelectedUsers(new Set());
    },
  });

  // Update admin status mutation
  const updateAdminMutation = useMutation({
    mutationFn: ({ userId, isAdmin }: { userId: string; isAdmin: boolean }) =>
      usersApi.updateAdminStatus(userId, isAdmin),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['user-stats'] });
    },
  });

  // Update role mutation
  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, roleLevel }: { userId: string; roleLevel: UserRoleLevel }) =>
      usersApi.updateUserRole(userId, roleLevel),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['user-stats'] });
    },
  });

  const handleSearch = () => {
    setParams((prev) => ({ ...prev, page: 1, search: search || undefined }));
  };

  const handleFilterChange = (filter: 'all' | 'beta' | 'non-beta') => {
    setFilterBetaTester(filter === 'beta' ? true : filter === 'non-beta' ? false : undefined);
    setParams((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage: number) => {
    setParams((prev) => ({ ...prev, page: newPage }));
  };

  const toggleUserSelection = (userId: string) => {
    const newSelection = new Set(selectedUsers);
    if (newSelection.has(userId)) {
      newSelection.delete(userId);
    } else {
      newSelection.add(userId);
    }
    setSelectedUsers(newSelection);
  };

  const selectAll = () => {
    if (usersData?.users) {
      setSelectedUsers(new Set(usersData.users.map((u) => u.user_id)));
    }
  };

  const clearSelection = () => {
    setSelectedUsers(new Set());
  };

  const handleBatchEnable = () => {
    if (selectedUsers.size > 0) {
      batchUpdateMutation.mutate({ userIds: Array.from(selectedUsers), isBetaTester: true });
    }
  };

  const handleBatchDisable = () => {
    if (selectedUsers.size > 0) {
      batchUpdateMutation.mutate({ userIds: Array.from(selectedUsers), isBetaTester: false });
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t.pageTitle}</h1>
            <p className="text-sm text-gray-500">{t.pageDescription}</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          {t.refresh}
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t.stats.totalUsers}</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_users}</p>
              </div>
              <Users className="w-10 h-10 text-blue-100" />
            </div>
          </Card>
          <Card className="bg-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t.stats.admins}</p>
                <p className="text-2xl font-bold text-red-600">{stats.total_admins}</p>
              </div>
              <Shield className="w-10 h-10 text-red-100" />
            </div>
          </Card>
          <Card className="bg-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t.stats.betaTesters}</p>
                <p className="text-2xl font-bold text-purple-600">{stats.total_beta_testers}</p>
              </div>
              <FlaskConical className="w-10 h-10 text-purple-100" />
            </div>
          </Card>
          <Card className="bg-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t.stats.roleDistribution}</p>
                <div className="flex gap-2 mt-1">
                  {Object.entries(stats.role_distribution || {}).map(([role, count]) => (
                    <span
                      key={role}
                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                        ROLE_COLORS[role as UserRoleLevel] || 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {t.roles[role as keyof typeof t.roles] || role}: {count}
                    </span>
                  ))}
                </div>
              </div>
              <Crown className="w-10 h-10 text-yellow-100" />
            </div>
          </Card>
        </div>
      )}

      {/* Filters and Search */}
      <Card className="bg-white">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 flex gap-2">
            <Input
              placeholder={t.filters.searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="flex-1"
            />
            <Button onClick={handleSearch}>
              <Search className="w-4 h-4" />
            </Button>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2">
            <Button
              variant={filterBetaTester === undefined ? 'primary' : 'outline'}
              size="sm"
              onClick={() => handleFilterChange('all')}
            >
              {t.filters.allUsers}
            </Button>
            <Button
              variant={filterBetaTester === true ? 'primary' : 'outline'}
              size="sm"
              onClick={() => handleFilterChange('beta')}
            >
              <FlaskConical className="w-4 h-4 mr-1" />
              {t.filters.betaTesters}
            </Button>
            <Button
              variant={filterBetaTester === false ? 'primary' : 'outline'}
              size="sm"
              onClick={() => handleFilterChange('non-beta')}
            >
              {t.filters.nonBeta}
            </Button>
          </div>
        </div>

        {/* Batch Actions */}
        {selectedUsers.size > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200 flex items-center gap-4">
            <span className="text-sm text-gray-600">
              {t.batch.selected.replace('{count}', String(selectedUsers.size))}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleBatchEnable}
              disabled={batchUpdateMutation.isPending}
            >
              <UserCheck className="w-4 h-4 mr-1" />
              {t.batch.enableBeta}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleBatchDisable}
              disabled={batchUpdateMutation.isPending}
            >
              <UserX className="w-4 h-4 mr-1" />
              {t.batch.disableBeta}
            </Button>
            <Button variant="ghost" size="sm" onClick={clearSelection}>
              {t.batch.clearSelection}
            </Button>
          </div>
        )}
      </Card>

      {/* Users Table */}
      <Card className="bg-white overflow-hidden">
        {isLoading && <Loading message={t.loading} />}

        {isError && (
          <ErrorMessage error={error instanceof Error ? error.message : t.error} />
        )}

        {usersData && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={
                          usersData.users.length > 0 &&
                          usersData.users.every((u) => selectedUsers.has(u.user_id))
                        }
                        onChange={(e) => (e.target.checked ? selectAll() : clearSelection())}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t.table.user}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t.table.contact}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t.table.role}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t.table.betaTester}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t.table.created}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t.table.actions}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {usersData.users.map((user: AdminUserListItem) => (
                    <tr key={user.user_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedUsers.has(user.user_id)}
                          onChange={() => toggleUserSelection(user.user_id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{user.display_name}</p>
                          <p className="text-sm text-gray-500">
                            {user.nickname !== user.display_name && <span>{user.nickname} · </span>}
                            @{user.username}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        <div className="space-y-0.5">
                          {user.email && <div>{user.email}</div>}
                          {user.phone_number && <div>{user.phone_number}</div>}
                          {!user.email && !user.phone_number && <span>-</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {/* Role Dropdown */}
                          <div className="relative">
                            <select
                              value={user.role_level}
                              onChange={(e) =>
                                updateRoleMutation.mutate({
                                  userId: user.user_id,
                                  roleLevel: e.target.value as UserRoleLevel,
                                })
                              }
                              disabled={updateRoleMutation.isPending}
                              className={`appearance-none pr-6 pl-2 py-0.5 rounded text-xs font-medium border-0 cursor-pointer ${
                                ROLE_COLORS[user.role_level as UserRoleLevel] || 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {(['free', 'vip', 'premium'] as UserRoleLevel[]).map((role) => (
                                <option key={role} value={role}>
                                  {t.roles[role]}
                                </option>
                              ))}
                            </select>
                            <ChevronDown className="w-3 h-3 absolute right-1 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500" />
                          </div>
                          {/* Admin Badge */}
                          {user.is_admin && (
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 flex items-center gap-1">
                              <Shield className="w-3 h-3" />
                              {t.roles.admin}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {user.is_beta_tester ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                            <Check className="w-3 h-3 mr-1" />
                            {t.status.beta}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                            <X className="w-3 h-3 mr-1" />
                            {t.status.notBeta}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          {/* Resources Button */}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setResourcesModalUser({ userId: user.user_id, userName: user.display_name })}
                            title={t.actions?.viewResources || '查看资源'}
                          >
                            <FolderOpen className="w-3 h-3 mr-1" />
                            {t.actions?.resources || '资源'}
                          </Button>
                          {/* Beta Toggle */}
                          <Button
                            variant={user.is_beta_tester ? 'outline' : 'primary'}
                            size="sm"
                            onClick={() =>
                              updateBetaTesterMutation.mutate({
                                userId: user.user_id,
                                isBetaTester: !user.is_beta_tester,
                              })
                            }
                            disabled={updateBetaTesterMutation.isPending}
                          >
                            <FlaskConical className="w-3 h-3 mr-1" />
                            {user.is_beta_tester ? t.actions.removeBeta : t.actions.makeBeta}
                          </Button>
                          {/* Admin Toggle */}
                          <Button
                            variant={user.is_admin ? 'danger' : 'outline'}
                            size="sm"
                            onClick={() =>
                              updateAdminMutation.mutate({
                                userId: user.user_id,
                                isAdmin: !user.is_admin,
                              })
                            }
                            disabled={updateAdminMutation.isPending}
                            title={user.is_admin ? t.actions.revokeAdmin : t.actions.makeAdmin}
                          >
                            {user.is_admin ? (
                              <>
                                <ShieldOff className="w-3 h-3 mr-1" />
                                {t.actions.revokeAdmin}
                              </>
                            ) : (
                              <>
                                <Shield className="w-3 h-3 mr-1" />
                                {t.actions.makeAdmin}
                              </>
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                {t.pagination.showing
                  .replace('{start}', String((params.page! - 1) * params.page_size! + 1))
                  .replace('{end}', String(Math.min(params.page! * params.page_size!, usersData.total)))
                  .replace('{total}', String(usersData.total))}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(params.page! - 1)}
                  disabled={params.page === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="px-3 py-1 text-sm text-gray-600">
                  {t.pagination.page
                    .replace('{current}', String(params.page))
                    .replace('{total}', String(Math.ceil(usersData.total / params.page_size!)))}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(params.page! + 1)}
                  disabled={!usersData.has_more}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* User Resources Modal */}
      <UserResourcesModal
        isOpen={!!resourcesModalUser}
        userId={resourcesModalUser?.userId || null}
        userName={resourcesModalUser?.userName}
        onClose={() => setResourcesModalUser(null)}
      />
    </div>
  );
}
